import React, { useState, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useSpinner } from '../utils/GlobalSpinner';
import { api } from '../services/api-client';
import NewWorkoutTable from '../components/workout/NewWorkoutTable';
import { useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import StudentDialog from '../components/dialogs/StudentDialog';
import { Tooltip } from 'primereact/tooltip';
import { CalendarTab } from '../components/client/CalendarTab';
import { ExcelView } from '../components/client/ExcelView';
import { DEFAULT_AVATAR } from '../utils/defaultAvatar';

export interface ClientData {
  id?: number;
  name?: string;
  email?: string;
  profileImage?: string;
  birthdate?: string;
  fitnessGoal?: string;
  activityLevel?: string;
  gender?: string;
  weight?: number;
  height?: number;
  contactMethod?: string;
  location?: string | null;
  coach?: {
    id: number | null;
  };
  user?: {
    subscription?: {
      status?: string;
    };
  };
  trainingType?: string;
}

interface PanelHeaderOptions {
  className: string;
}

const ClientDashboard: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [cycleOptions, setCycleOptions] = useState<Array<{ id: number; name: string }>>([]);
  const { showToast } = useToast();
  const { setLoading } = useSpinner();
  const intl = useIntl();
  const [isNewStudentDialogVisible, setIsNewStudentDialogVisible] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(1);
  const [isExcelOnlyMode, setIsExcelOnlyMode] = useState<boolean>(false);

  // Fetch data when the component mounts or refreshKey changes
  useEffect(() => {
    if (!clientId) return;

    setLoading(true);

    api.user
      .fetchClientByClientId(Number(clientId))
      .then(({ data }) => {
        setClientData(data);
      })
      .catch((error: Error) => {
        showToast('error', 'Error fetching client data', error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [clientId, refreshKey, setLoading, showToast]);

  // Fetch cycles for the Excel view tab. We do this once per client (not gated
  // on refreshKey — Excel view has its own refresh button).
  useEffect(() => {
    if (!clientId) return;
    api.workout
      .fetchTrainingCyclesByClient(Number(clientId))
      .then(({ data }) => {
        const cycles = (data ?? []).map((c: { id: number; name: string }) => ({
          id: c.id,
          name: c.name
        }));
        setCycleOptions(cycles);
      })
      .catch(() => {
        // Non-fatal: Excel view will show empty state.
        setCycleOptions([]);
      });
  }, [clientId, refreshKey]);

  const handleNewStudentDialogHide = (): void => {
    setIsNewStudentDialogVisible(false);
  };

  const handleNewStudentDialogShow = (): void => {
    setIsNewStudentDialogVisible(true);
  };

  const renderTabView = (): React.JSX.Element => {
    return (
      <TabView>
        <TabPanel header={intl.formatMessage({ id: 'clientDashboard.tabs.calendar' })}>
          {clientData && (
            <CalendarTab
              clientId={clientId || ''}
              clientData={clientData}
              refreshKey={refreshKey}
              setRefreshKey={setRefreshKey}
            />
          )}
        </TabPanel>

        <TabPanel header={intl.formatMessage({ id: 'clientDashboard.tabs.excelView' })}>
          <ExcelView cycleOptions={cycleOptions} />
        </TabPanel>
        {/*
          Tabs "Dashboard / Historia Clínica / Datos del Usuario" estaban en producción
          como placeholders "Próximamente". Se ocultaron al cerrar Bug 09 del
          qa-audit/2026-05-21 — generaban expectativa que terminaba en vacío.
          Cuando se implementen, restaurar acá.
        */}
      </TabView>
    );
  };

  const calculateAge = (birthdate: string | undefined): number | null => {
    if (!birthdate) return null;

    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  // Header personalizado para el panel principal
  const headerTemplate = (options: PanelHeaderOptions): React.JSX.Element => {
    const className = `${options.className} flex justify-content-start align-items-center`;
    return (
      <div className={className}>
        <div className="flex align-items-center gap-3">
          <div className="relative">
            <img
              src={clientData?.profileImage || DEFAULT_AVATAR}
              alt={clientData?.name || 'Profile'}
              className="w-4rem h-4rem border-circle"
              style={{ objectFit: 'cover' }}
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                // #37: asset propio, no una foto de stock de un CDN ajeno.
                e.currentTarget.src = DEFAULT_AVATAR;
              }}
            />
            {clientData &&
              [
                'fitnessGoal',
                'activityLevel',
                'gender',
                'weight',
                'height',
                'birthdate',
                'contactMethod',
                'location'
              ].some((field) => !clientData[field as keyof ClientData]) && (
                <>
                  <Tooltip target=".missing-data-indicator" />
                  <div
                    className="missing-data-indicator absolute bottom-0 right-0 bg-white border-circle w-1rem h-1rem flex align-items-center justify-content-center border-1 border-red-500"
                    data-pr-tooltip={intl.formatMessage({ id: 'common.missingData' })}
                    data-pr-position="bottom"
                  >
                    <i className="pi pi-exclamation-triangle text-red-500 text-xs"></i>
                  </div>
                </>
              )}
          </div>
          <div className="flex flex-column">
            <h4 className="m-0 text-xl font-bold">{clientData?.name}</h4>
            {clientData?.birthdate && (
              <p className="m-0 text-600 text-sm">
                {intl.formatMessage({ id: 'common.age' })}:&nbsp;
                {calculateAge(clientData.birthdate)}&nbsp;
                {intl.formatMessage({ id: 'common.years' })}
              </p>
            )}
          </div>
        </div>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleNewStudentDialogShow()}
          tooltip={intl.formatMessage({ id: 'students.actions.editProfile' })}
        />
      </div>
    );
  };

  return (
    <div className="client-dashboard p-1">
      {isExcelOnlyMode ? (
        // Modo solo Excel - pantalla completa sin header
        <div className="excel-fullscreen-mode">
          {clientData ? (
            <NewWorkoutTable
              cycleOptions={[]}
              clientData={clientData}
              isExcelOnlyMode={true}
              clientName={clientData?.name}
              onToggleExcelMode={() => setIsExcelOnlyMode(false)}
            />
          ) : (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
              <div className="text-center">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                <p>{intl.formatMessage({ id: 'common.loading' }, { defaultMessage: 'Cargando...' })}</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Modo dashboard completo
        <>
          <Panel headerTemplate={headerTemplate} className="panel-client-dashboard">
            {renderTabView()}
          </Panel>
        </>
      )}

      <Dialog
        header={intl.formatMessage({ id: 'students.dialog.editProfile' })}
        visible={isNewStudentDialogVisible}
        onHide={handleNewStudentDialogHide}
        draggable={false}
        resizable={false}
        dismissableMask
        className="responsive-dialog"
        style={{ width: '50vw' }}
      >
        <StudentDialog
          onClose={handleNewStudentDialogHide}
          setRefreshKey={setRefreshKey}
          studentData={clientData}
          visible={isNewStudentDialogVisible}
        />
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
